import React, { useEffect, useState } from 'react';
import { useQueryParams, StringParam } from 'use-query-params';
import { MyShiftScreen } from './MyShifts/MyShiftScreen';
import { AllActivitiesScreen } from './AllActivities/AllActivitiesScreen';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { useUser } from '../../../global/auth/useUser';
import {
  GetVolunteerEnrolmentsCount,
  GetVolunteerEnrolmentsCountVariables,
} from './ActivityQueries/__generated__/GetVolunteerEnrolmentsCount';
import { GET_VOLUNTEER_ENROLMENTS_COUNT } from './ActivityQueries/get-volunteer-enrolments.gql';
import { CommonScreenSkeleton } from '../../../common/CommonScreenSkeleton';
import { ActivityContext } from './ActivityContext';
import { Page } from '../../../global/components';
import { ActivitiesTutorialDialog } from './ActivitiesTutorialDialog';
import { AllProgramsScreen } from './AllPrograms/AllProgramsScreen';
import { RolesScreen } from '../roles/RolesScreen';
import { useVisibleExploreTabs } from './use-visible-explore-tabs';
import { ActivityTabValue } from './ActivityTypes';

export const ActivitiesInnerScreen = () => {
  const [query, setQuery] = useQueryParams({
    tab: StringParam,
  });

  const { getVolunteerIdentity } = useUser();
  const { volunteerId } = getVolunteerIdentity();

  const { data: activityEnrolments, loading } = useCampfireQuery<
    GetVolunteerEnrolmentsCount,
    GetVolunteerEnrolmentsCountVariables
  >(GET_VOLUNTEER_ENROLMENTS_COUNT, {
    options: {
      variables: {
        volunteerId,
      },
    },
  });

  const visibleTabs = useVisibleExploreTabs();

  const hasActivityEnrolments =
    activityEnrolments?.vm.volunteer?.activityEnrolments &&
    activityEnrolments?.vm.volunteer?.activityEnrolments.length > 0;

  const requestedTab = (query.tab ?? (hasActivityEnrolments ? 'mine' : 'all')) as ActivityTabValue;
  const activeTab = visibleTabs.includes(requestedTab) ? requestedTab : visibleTabs[0] ?? 'all';

  useEffect(() => {
    if (loading) return;
    if (query.tab !== activeTab && (query.tab || activeTab !== 'all')) setQuery({ tab: activeTab });
  }, [loading, activeTab, query]);

  if (loading) {
    return <CommonScreenSkeleton />;
  }

  if (activeTab === 'programs') {
    return (
      <ActivityContext>
        <AllProgramsScreen />
      </ActivityContext>
    );
  }

  if (activeTab === 'mine') {
    return (
      <ActivityContext>
        <MyShiftScreen />
      </ActivityContext>
    );
  }

  if (activeTab === 'roles') {
    return (
      <ActivityContext>
        <RolesScreen />
      </ActivityContext>
    );
  }

  return (
    <ActivityContext>
      <AllActivitiesScreen />
    </ActivityContext>
  );
};

export const ActivitiesV2Screen = () => {
  const [tutorialDialogOpen, setTutorialDialogOpen] = useState<boolean>(false);

  return (
    <Page
      pageHelpOptions={{
        onClick: () => setTutorialDialogOpen(true),
      }}
    >
      <ActivitiesInnerScreen />
      <ActivitiesTutorialDialog
        open={tutorialDialogOpen}
        onClose={() => {
          setTutorialDialogOpen(false);
        }}
      />
    </Page>
  );
};
